/** Phase 3 data: shared inbox, channels, lead capture, automations, sequences and privacy. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, qs } from "./client";

type UserRef = { id: string; full_name: string };

export interface Channel {
  id: string; kind: "whatsapp" | "email" | "indiamart" | "sms" | "phone"; name: string; is_active: boolean;
  config: Record<string, string>; default_owner: UserRef | null; has_secrets: boolean;
  first_response_minutes: number | null; escalate_to: UserRef | null;
  webhook_url: string; verify_token: string | null; last_inbound_at: string | null; created_at: string;
  mailbox_available?: { gmail: boolean; microsoft: boolean };
}
export interface ConversationContact { id: string; full_name: string; email: string | null; phone: string | null; whatsapp_opt_in: boolean; email_opt_out: boolean }
export interface Conversation {
  id: string; channel: { id: string; kind: Channel["kind"]; name: string }; contact: ConversationContact | null;
  external_id: string; subject: string | null; status: "open" | "closed"; assignee: UserRef | null;
  last_message_at: string; last_inbound_at: string | null; last_preview: string; unread_count: number;
  window_open: boolean; window_closes_at: string | null;
  awaiting_since: string | null; response_due_at: string | null; sla_breached_at: string | null;
}
export interface Message {
  id: string; direction: "in" | "out"; body: string; template_name: string | null;
  status: "received" | "queued" | "sent" | "delivered" | "read" | "failed"; error: string | null;
  origin: "manual" | "automation" | "sequence"; sender: UserRef | null; created_at: string; sent_at: string | null;
  response_seconds?: number | null; sla_met?: boolean | null;
}
export interface ConversationDetail extends Conversation { messages: Message[] }

export interface FormField { key: string; label: string; type: "text" | "email" | "phone" | "textarea" | "select" | "checkbox"; required: boolean; options: string[] }
export interface WebForm {
  id: string; name: string; token: string; is_active: boolean; title: string; intro: string | null; fields: FormField[];
  create_deal: boolean; owner: UserRef | null; consent_text: string | null; success_message: string; redirect_url: string | null;
  submission_count: number; public_url: string; embed_code: string; created_at: string;
}
export interface PublicForm { title: string; intro: string | null; fields: FormField[]; consent_text: string | null; company: string }
export interface LeadCapture { id: string; source_type: "web_form" | "indiamart"; status: "created" | "updated" | "spam" | "failed"; data: Record<string, string>; error: string | null; created_at: string; contact_id: string | null; deal_id: string | null }

export interface Condition { field: string; op: string; value: unknown }
export interface Action { type: string; params: Record<string, unknown> }
export interface Rule {
  id: string; name: string; description: string | null; is_active: boolean; trigger: string;
  conditions: Condition[]; actions: Action[]; flow: FlowStep[]; run_count: number; last_run_at: string | null; created_by: UserRef | null; created_at: string;
}
/** One box in a workflow: do something, branch on conditions, or wait. */
export type FlowStep =
  | { type: "action"; action: Action }
  | { type: "condition"; conditions: Condition[]; match: "all" | "any"; yes: FlowStep[]; no: FlowStep[] }
  | { type: "wait"; wait_hours: number };
export interface Run { id: string; event_type: string; status: "pending" | "waiting" | "succeeded" | "failed" | "skipped"; depth: number; resume_at: string | null; log: Record<string, unknown>[]; error: string | null; created_at: string; finished_at: string | null }

export interface Step { kind: "email" | "whatsapp" | "task"; wait_hours: number; subject?: string | null; body?: string | null; template_name?: string | null; template_language?: string; template_params?: string[] }
export interface Sequence { id: string; name: string; description: string | null; is_active: boolean; steps: Step[]; stop_on_reply: boolean; created_at: string; active_count: number; completed_count: number; stopped_count: number }
export interface Enrolment {
  id: string; sequence: { id: string; name: string }; contact: { id: string; full_name: string }; status: "active" | "completed" | "stopped";
  current_step: number; next_run_at: string | null; stop_reason: string | null; log: { step: number; kind: string; status: string; result: string; at: string }[];
  enrolled_by: UserRef | null; created_at: string;
}

type Body = Record<string, unknown>;

function useInvalidate() {
  const qc = useQueryClient();
  return (...prefixes: string[]) => prefixes.forEach((p) => void qc.invalidateQueries({ queryKey: [p] }));
}

// ---- inbox ----
export const useConversations = (filters: { status?: string; mine?: boolean; channel?: string; contact_id?: string }, enabled = true) =>
  useQuery({ queryKey: ["conversations", filters], queryFn: () => api<Conversation[]>("GET", `/conversations${qs(filters)}`), refetchInterval: 5000, enabled });
export const useConversation = (id: string | null) =>
  useQuery({ queryKey: ["conversation", id], queryFn: () => api<ConversationDetail>("GET", `/conversations/${id}`), enabled: !!id,
    refetchInterval: (q) => (q.state.data?.messages.some((m) => m.status === "queued") ? 1500 : 5000) });
export const useUnread = (enabled: boolean) =>
  useQuery({ queryKey: ["unread"], queryFn: () => api<{ unread: number }>("GET", "/conversations/unread"), refetchInterval: 10000, enabled });

export function useInboxAction() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ path, method = "POST", body }: { path: string; method?: "POST" | "PATCH"; body?: Body }) => api<unknown>(method, path, body),
    onSuccess: () => invalidate("conversations", "conversation", "unread", "timeline", "contact"),
  });
}

export const useChannels = () => useQuery({ queryKey: ["channels"], queryFn: () => api<Channel[]>("GET", "/channels") });
export function useSaveChannel(id?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Body) => (id ? api<Channel>("PATCH", `/channels/${id}`, data) : api<Channel>("POST", "/channels", data)),
    onSuccess: () => invalidate("channels"),
  });
}

// ---- lead capture ----
export const useForms = () => useQuery({ queryKey: ["forms"], queryFn: () => api<WebForm[]>("GET", "/forms") });
export function useSaveForm(id?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Body) => (id ? api<WebForm>("PUT", `/forms/${id}`, data) : api<WebForm>("POST", "/forms", data)),
    onSuccess: () => invalidate("forms"),
  });
}
export const useLeadLog = () => useQuery({ queryKey: ["leads"], queryFn: () => api<LeadCapture[]>("GET", "/leads"), refetchInterval: 10000 });
export const usePublicForm = (token: string) =>
  useQuery({ queryKey: ["public-form", token], queryFn: () => api<PublicForm>("GET", `/public/forms/${token}`), retry: false });

// ---- automations ----
export const useTriggers = () => useQuery({ queryKey: ["triggers"], queryFn: () => api<Record<string, string>>("GET", "/automations/triggers"), staleTime: Infinity });
export const useRules = () => useQuery({ queryKey: ["automations"], queryFn: () => api<Rule[]>("GET", "/automations") });
export const useRuns = (id: string | null) =>
  useQuery({ queryKey: ["runs", id], queryFn: () => api<Run[]>("GET", `/automations/${id}/runs`), enabled: !!id, refetchInterval: 5000 });
export function useSaveRule(id?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Body) => (id ? api<Rule>("PUT", `/automations/${id}`, data) : api<Rule>("POST", "/automations", data)),
    onSuccess: () => invalidate("automations"),
  });
}
export function useDeleteRule() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => api<void>("DELETE", `/automations/${id}`), onSuccess: () => invalidate("automations") });
}

// ---- sequences ----
export const useSequences = (enabled = true) => useQuery({ queryKey: ["sequences"], queryFn: () => api<Sequence[]>("GET", "/sequences"), enabled });
export function useSaveSequence(id?: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Body) => (id ? api<Sequence>("PUT", `/sequences/${id}`, data) : api<Sequence>("POST", "/sequences", data)),
    onSuccess: () => invalidate("sequences"),
  });
}
export const useEnrolments = (sequenceId: string | null) =>
  useQuery({ queryKey: ["enrolments", sequenceId], queryFn: () => api<Enrolment[]>("GET", `/sequences/${sequenceId}/enrolments`), enabled: !!sequenceId, refetchInterval: 5000 });
export const useContactEnrolments = (contactId: string, enabled: boolean) =>
  useQuery({ queryKey: ["enrolments", "contact", contactId], queryFn: () => api<Enrolment[]>("GET", `/sequences/enrolments/by-contact/${contactId}`), enabled });
export function useEnrol() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ sequenceId, contactIds }: { sequenceId: string; contactIds: string[] }) =>
      api<{ enrolled: Enrolment[]; skipped: { contact_id: string; reason: string }[] }>("POST", `/sequences/${sequenceId}/enrolments`, { contact_ids: contactIds }),
    onSuccess: () => invalidate("enrolments", "sequences"),
  });
}
export function useStopEnrolment() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => api<Enrolment>("POST", `/sequences/enrolments/${id}/stop`), onSuccess: () => invalidate("enrolments", "sequences") });
}

// ---- privacy ----
export async function downloadContactExport(id: string, name: string): Promise<void> {
  const data = await api<unknown>("GET", `/contacts/${id}/export`);
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: `${name.replace(/\W+/g, "-").toLowerCase()}-data.json` });
  a.click();
  URL.revokeObjectURL(url);
}
export function useEraseContact() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: ({ id, confirm }: { id: string; confirm: string }) => api<void>("POST", `/contacts/${id}/erase`, { confirm }), onSuccess: () => invalidate("contacts", "contact") });
}
